const CATALOG_CACHE_TTL_MS = 2 * 60 * 1000;
const PRODUCT_CACHE_TTL_MS = 5 * 60 * 1000;
const SESSION_STORAGE_KEY = 'oakley-public-cache-v1';

const memoryCache = {
  catalogs: new Map(),
  products: new Map(),
};

const inflightRequests = new Map();

const canUseStorage = () => typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';

const readPersistedCache = () => {
  if (!canUseStorage()) {
    return { catalogs: {}, products: {} };
  }

  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) {
      return { catalogs: {}, products: {} };
    }

    const parsed = JSON.parse(raw);
    return {
      catalogs: parsed?.catalogs && typeof parsed.catalogs === 'object' ? parsed.catalogs : {},
      products: parsed?.products && typeof parsed.products === 'object' ? parsed.products : {},
    };
  } catch {
    return { catalogs: {}, products: {} };
  }
};

const writePersistedCache = () => {
  if (!canUseStorage()) {
    return;
  }

  try {
    const catalogs = Object.fromEntries(memoryCache.catalogs.entries());
    const products = Object.fromEntries(memoryCache.products.entries());

    window.sessionStorage.setItem(
      SESSION_STORAGE_KEY,
      JSON.stringify({ catalogs, products })
    );
  } catch {
    // Ignore storage write failures so public browsing remains resilient.
  }
};

const hydrateCacheFromStorage = () => {
  if (memoryCache.catalogs.size > 0 || memoryCache.products.size > 0) {
    return;
  }

  const persisted = readPersistedCache();
  Object.entries(persisted.catalogs).forEach(([key, value]) => {
    if (value?.data) {
      memoryCache.catalogs.set(key, value);
    }
  });
  Object.entries(persisted.products).forEach(([key, value]) => {
    if (value?.data) {
      memoryCache.products.set(key, value);
    }
  });
};

const getProductId = (product) => product?._id ?? product?.id ?? null;

const buildProductSnapshot = (product) => {
  if (!product || typeof product !== 'object') {
    return null;
  }

  const productId = getProductId(product);
  if (!productId) {
    return null;
  }

  const normalizedImages = Array.isArray(product.images) && product.images.length > 0
    ? product.images.filter(Boolean).slice(0, 5)
    : [product.imageUrl || product.image_url || product.image].filter(Boolean);

  const primaryImage = normalizedImages[0] || product.imageUrl || product.image_url || product.image || null;

  return {
    ...product,
    id: product.id ?? productId,
    _id: productId,
    imageUrl: product.imageUrl ?? primaryImage,
    image_url: product.image_url ?? primaryImage,
    image: product.image ?? primaryImage,
    images: normalizedImages,
  };
};

const getCacheEntry = (bucket, key) => {
  hydrateCacheFromStorage();
  return memoryCache[bucket].get(key) || null;
};

const setCacheEntry = (bucket, key, data) => {
  memoryCache[bucket].set(key, {
    data,
    timestamp: Date.now(),
  });
  writePersistedCache();
};

const isFresh = (entry, ttlMs) =>
  Boolean(entry?.timestamp && Date.now() - entry.timestamp <= ttlMs);

const dedupeRequest = async (requestKey, fetcher) => {
  if (inflightRequests.has(requestKey)) {
    return inflightRequests.get(requestKey);
  }

  const requestPromise = Promise.resolve()
    .then(fetcher)
    .finally(() => {
      inflightRequests.delete(requestKey);
    });

  inflightRequests.set(requestKey, requestPromise);
  return requestPromise;
};

export const getCachedCatalogProducts = (cacheKey = 'default') => {
  const entry = getCacheEntry('catalogs', cacheKey);
  if (!entry?.data) {
    return null;
  }

  return {
    data: entry.data,
    isFresh: isFresh(entry, CATALOG_CACHE_TTL_MS),
  };
};

export const getCachedProductDetail = (productId) => {
  if (!productId) {
    return null;
  }

  const entry = getCacheEntry('products', String(productId));
  if (!entry?.data) {
    return null;
  }

  return {
    data: entry.data,
    isFresh: isFresh(entry, PRODUCT_CACHE_TTL_MS),
  };
};

export const primeProductDetailCache = (product) => {
  const snapshot = buildProductSnapshot(product);
  const productId = getProductId(snapshot);

  if (!snapshot || !productId) {
    return;
  }

  setCacheEntry('products', String(productId), snapshot);
};

export const primeCatalogProductsCache = (products, cacheKey = 'default') => {
  const normalizedProducts = Array.isArray(products)
    ? products.map(buildProductSnapshot).filter(Boolean)
    : [];

  setCacheEntry('catalogs', cacheKey, normalizedProducts);
  normalizedProducts.forEach((product) => {
    primeProductDetailCache(product);
  });
};

export const loadCatalogProducts = async (fetcher, { cacheKey = 'default' } = {}) => {
  const cached = getCachedCatalogProducts(cacheKey);
  const requestKey = `catalog:${cacheKey}`;

  const fetchAndStore = async () => {
    const result = await fetcher();
    primeCatalogProductsCache(result, cacheKey);
    return result;
  };

  if (cached?.isFresh) {
    return {
      data: cached.data,
      source: 'cache',
      revalidate: null,
    };
  }

  const revalidate = dedupeRequest(requestKey, fetchAndStore);

  if (cached?.data?.length) {
    return {
      data: cached.data,
      source: 'stale-cache',
      revalidate,
    };
  }

  const data = await revalidate;
  return {
    data,
    source: 'network',
    revalidate: null,
  };
};

export const loadProductDetail = async (productId, fetcher) => {
  const cacheKey = String(productId);
  const cached = getCachedProductDetail(cacheKey);
  const requestKey = `product:${cacheKey}`;

  const fetchAndStore = async () => {
    const result = await fetcher();
    const snapshot = buildProductSnapshot(result);
    if (snapshot) {
      primeProductDetailCache(snapshot);
      return snapshot;
    }
    return result;
  };

  if (cached?.isFresh) {
    return {
      data: cached.data,
      source: 'cache',
      revalidate: null,
    };
  }

  const revalidate = dedupeRequest(requestKey, fetchAndStore);

  if (cached?.data) {
    return {
      data: cached.data,
      source: 'stale-cache',
      revalidate,
    };
  }

  const data = await revalidate;
  return {
    data,
    source: 'network',
    revalidate: null,
  };
};

export const prefetchProductDetail = async (productId, fetcher) => {
  if (!productId) {
    return null;
  }

  const cached = getCachedProductDetail(productId);
  if (cached?.isFresh) {
    return cached.data;
  }

  return dedupeRequest(`product:${String(productId)}`, async () => {
    const result = await fetcher();
    const snapshot = buildProductSnapshot(result);
    if (snapshot) {
      primeProductDetailCache(snapshot);
      return snapshot;
    }
    return result;
  });
};
